import { useCallback, useEffect, useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError, type ReferenceLibraryQuery } from '../../lib/api.js';

/**
 * THE LIBRARY'S READS, keyed under `['references', projectId]` so one
 * invalidation after any write refreshes the list, the sidebar's counts, the
 * open reference and the "References · n" entries elsewhere together.
 *
 * Previews are signed URLs that live an hour (design §5): every read that can
 * carry one refetches well before that, so a tab left open over lunch does not
 * come back to broken pictures.
 */
export const PREVIEW_URL_REFRESH_MS = 45 * 60_000;

export const PAGE_SIZE = 30;

export function referencesKey(projectId: string) {
  return ['references', projectId] as const;
}

export function useReferenceMeta(projectId: string) {
  return useQuery({
    queryKey: [...referencesKey(projectId), 'meta'],
    queryFn: () => api.referenceMeta(projectId),
    enabled: projectId !== '',
    staleTime: 30_000,
  });
}

export function useReferenceLibrary(projectId: string, query: ReferenceLibraryQuery) {
  return useInfiniteQuery({
    queryKey: [...referencesKey(projectId), 'list', query],
    queryFn: ({ pageParam }) =>
      api.referenceLibrary(projectId, { ...query, limit: PAGE_SIZE, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: projectId !== '',
    refetchInterval: PREVIEW_URL_REFRESH_MS,
  });
}

export function useReferenceDetail(projectId: string, referenceId: string | null) {
  return useQuery({
    queryKey: [...referencesKey(projectId), 'detail', referenceId],
    queryFn: () => api.reference(projectId, referenceId ?? '', { large: true }),
    enabled: projectId !== '' && referenceId !== null,
    refetchInterval: PREVIEW_URL_REFRESH_MS,
    retry: false,
  });
}

/** One invalidation for every read of the library. */
export function useInvalidateReferences(projectId: string) {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: referencesKey(projectId) }),
    [queryClient, projectId],
  );
}

/** The value a few hundred milliseconds after it stopped changing. */
export function useDebounced<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => {
      setSettled(value);
    }, delayMs);
    return () => {
      clearTimeout(timer);
    };
  }, [value, delayMs]);
  return settled;
}

/** The server's sentence when it gave one, else ours. */
export function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError && error.message !== '' ? error.message : fallback;
}

/** A request key for idempotent creates: one per thing being created, reused on a retry. */
export { newRequestKey } from '../conversations/hooks.js';
